import {
  Correlation,
  EntityType,
  PathResult,
  TreeNode,
  ViewerEdge,
  ViewerGraphData,
  ViewerNode,
} from '../models/graph-viewer.models';
import { resolveBrandKeyFromPlatform } from '../../../../services/link-analysis/platform-icon.util';
import { isAggregateHubNode } from '../../../../services/link-analysis/link-analysis-searchable.util';
import { foldCollectedPhotoNodes } from './graph-collected-photos.util';

export const ENTITY_COLORS: Record<EntityType, string> = {
  person: '#4f7dc9',
  email: '#2aa8a0',
  phone: '#2ea043',
  social: '#7c5cbf',
  location: '#c45c4a',
  image: '#5b8fb9',
};

export const ENTITY_LABELS: Record<EntityType, string> = {
  person: 'Person',
  email: 'Email',
  phone: 'Phone',
  social: 'Social Profile',
  location: 'Location',
  image: 'Image',
};

export const ENTITY_ICONS: Record<EntityType, string> = {
  person: '👤',
  email: '✉',
  phone: '📱',
  social: '🌐',
  location: '📍',
  image: '🖼',
};

export const LAYER_ORDER: EntityType[] = ['person', 'email', 'phone', 'social', 'location', 'image'];

export const PLATFORM_BRAND: Record<string, { color: string; short: string }> = {
  truecaller: { color: '#0087FF', short: 'TC' },
  facebook: { color: '#1877F2', short: 'f' },
  instagram: { color: '#E4405F', short: 'IG' },
  microsoft: { color: '#00A4EF', short: 'MS' },
  twitter: { color: '#1D1D1F', short: 'X' },
  hpgas: { color: '#E4002B', short: 'HP' },
  maps: { color: '#34A853', short: 'M' },
  google: { color: '#4285F4', short: 'G' },
  hackerearth: { color: '#2C3454', short: 'HE' },
  quillbot: { color: '#2BAD6E', short: 'QB' },
  edx: { color: '#02262B', short: 'eX' },
  fiverr: { color: '#1DBF73', short: 'fi' },
  freelancer: { color: '#29B2FE', short: 'Fl' },
  grammarly: { color: '#15C39A', short: 'Gr' },
  hackerrank: { color: '#00EA64', short: 'HR' },
  naukri: { color: '#4A90E2', short: 'N' },
  quora: { color: '#B92B27', short: 'Q' },
  replit: { color: '#F26207', short: 'Rl' },
  spotify: { color: '#1DBF73', short: 'Sp' },
  skype: { color: '#00AFF0', short: 'Sk' },
};

function mapApiType(type: string, role?: string): EntityType {
  const normalized = String(type || role || '').toLowerCase();
  const map: Record<string, EntityType> = {
    person: 'person',
    email: 'email',
    email_hint: 'email',
    phone: 'phone',
    phone_hint: 'phone',
    social: 'social',
    social_profile: 'social',
    location: 'location',
    address: 'location',
    image: 'image',
    face_result: 'image',
    username: 'social',
    website: 'social',
    osint_source: 'social',
    leak_source: 'social',
    starter: 'phone',
    query_root: 'phone',
    face_image: 'image',
    image_query: 'image',
    ai_analysis: 'person',
    collection: 'email',
    collected_emails: 'email',
    collected_phones: 'phone',
    collected_usernames: 'social',
    collected_images: 'image',
    aggregate_phones: 'phone',
    aggregate_emails: 'email',
    aggregate_names: 'person',
    aggregate_photos: 'image',
    person_candidate: 'person',
    confirmed_person: 'person',
  };
  if (normalized === 'query_root') {
    const queryType = String(role || '').toLowerCase();
    if (queryType === 'email') return 'email';
    if (queryType === 'username') return 'social';
    if (queryType === 'image') return 'image';
    return 'phone';
  }
  return map[normalized] || 'social';
}

function normalizeEdgeEndpoint(value: unknown): string {
  if (value == null) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'object' && 'id' in (value as object)) {
    return String((value as { id?: string }).id || '');
  }
  return String(value);
}

function extractDetails(node: any): Record<string, string> {
  const details: Record<string, string> = {};
  const metadata = node?.metadata || {};
  Object.entries(metadata).forEach(([key, value]) => {
    if (value == null) return;
    if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
      details[key] = String(value);
    }
  });
  if (node?.role) details['role'] = String(node.role);
  if (node?.evidenceTier) details['evidenceTier'] = String(node.evidenceTier);
  if (node?.attributionStatus) details['status'] = String(node.attributionStatus);
  if (metadata.platform) details['platform'] = String(metadata.platform);
  if (metadata.handle) details['username'] = String(metadata.handle);
  if (metadata.queryValue) details['value'] = String(metadata.queryValue);
  return details;
}

function extractImage(node: any): string | undefined {
  const metadata = node?.metadata || {};
  const primaryMatch =
    Array.isArray(metadata.matches) && metadata.matches.length
      ? metadata.matches[0]
      : Array.isArray(metadata.results) && metadata.results.length
        ? metadata.results[0]
        : null;
  return (
    node?.imageUrl ||
    metadata.matchImageUrl ||
    primaryMatch?.display_image_url ||
    primaryMatch?.image_url ||
    metadata.previewImageUrl ||
    metadata.logo ||
    metadata.sourceImageUrl ||
    metadata.imageUrl ||
    metadata.thumbnail ||
    metadata.sourceData?.logo ||
    metadata.leakData?.logo ||
    undefined
  );
}

function extractCoords(node: any): [number, number] | undefined {
  const metadata = node?.metadata || {};
  if (Array.isArray(metadata.coords) && metadata.coords.length >= 2) {
    const lat = Number(metadata.coords[0]);
    const lng = Number(metadata.coords[1]);
    if (Number.isFinite(lat) && Number.isFinite(lng)) return [lat, lng];
  }
  if (metadata.lat != null && metadata.lng != null) {
    const lat = Number(metadata.lat);
    const lng = Number(metadata.lng);
    if (Number.isFinite(lat) && Number.isFinite(lng)) return [lat, lng];
  }

  const latLngHits: Array<{ lat: number; lng: number }> = [];
  const collectLatLng = (value: unknown): void => {
    if (value == null) return;
    if (Array.isArray(value)) {
      if (
        value.length >= 2 &&
        typeof value[0] === 'number' &&
        typeof value[1] === 'number' &&
        Math.abs(value[0]) <= 90 &&
        Math.abs(value[1]) <= 180
      ) {
        latLngHits.push({ lat: value[0], lng: value[1] });
        return;
      }
      value.forEach(collectLatLng);
      return;
    }
    if (typeof value !== 'object') return;
    const obj = value as Record<string, unknown>;
    if (Array.isArray(obj['lat_lng']) && obj['lat_lng'].length >= 2) {
      const lat = Number(obj['lat_lng'][0]);
      const lng = Number(obj['lat_lng'][1]);
      if (Number.isFinite(lat) && Number.isFinite(lng) && Math.abs(lat) <= 90 && Math.abs(lng) <= 180) {
        latLngHits.push({ lat, lng });
      }
    }
    Object.values(obj).forEach(collectLatLng);
  };

  collectLatLng(metadata.sourceData);
  if (latLngHits.length) return [latLngHits[0].lat, latLngHits[0].lng];
  return undefined;
}

function resolveGraphRootValue(payload: any, fallback = ''): string {
  const candidates = [
    fallback,
    payload?.summary?.rootValue,
    payload?.investigation?.rootValue,
    payload?.investigation?.root_value,
    payload?.rootValue,
    payload?.root_value,
  ];
  for (const value of candidates) {
    const text = String(value || '').trim();
    if (text) return text;
  }
  return '';
}

function isStarterGraphNode(node: any): boolean {
  return (
    String(node?.type || '').toLowerCase() === 'starter' ||
    node?.metadata?.isStarter === true ||
    String(node?.id || '') === 'starter'
  );
}

/**
 * Phase 7: disabled. Never collapse “same phone/email as root” nodes or rewrite
 * parentNodeId / edges onto query_root. Ladder parentage is R13 only.
 */
export function collapseDuplicateRootValueNodes(
  nodes: any[],
  edges: any[],
  _options?: { rootId?: string; rootValue?: string },
): { nodes: any[]; edges: any[] } {
  return {
    nodes: Array.isArray(nodes) ? nodes : [],
    edges: Array.isArray(edges) ? edges : [],
  };
}

/** True when the payload already has ladder-scoped Names/Emails/Phones aggregates. */
function hasLadderAggregateNodes(nodes: any[]): boolean {
  return (nodes || []).some((node) => {
    const type = String(node?.type || '').toLowerCase();
    if (
      type !== 'aggregate_names' &&
      type !== 'aggregate_emails' &&
      type !== 'aggregate_phones'
    ) {
      return false;
    }
    const id = String(node?.id || '');
    return (
      /^aggregate:\d+:(names|emails|phones):/.test(id) ||
      Array.isArray(node?.metadata?.entries) ||
      Array.isArray(node?.metadata?.availableActions)
    );
  });
}

function isMaskedPersonName(value: string): boolean {
  const text = String(value || '').trim();
  if (!text || text.length < 2) return true;
  return /\.{2,}|\*{2,}/.test(text);
}

function isCollectablePhotoUrl(url: string, node?: any): boolean {
  const value = String(url || '').trim();
  if (!/^https?:\/\//i.test(value)) return false;
  if (value.startsWith('data:image/svg')) return false;
  const type = String(node?.type || '').toLowerCase();
  if (type === 'leak_source') return false;
  const logo = String(node?.metadata?.logo || '').trim();
  if (logo && value === logo) return false;
  return true;
}

function collectNamedItems(
  nodes: any[],
  kind: 'name' | 'photo',
): Array<{ value: string; sources: string[] }> {
  const map = new Map<string, { value: string; sources: Set<string> }>();
  const add = (raw: unknown, sourceName: string) => {
    const value = String(raw || '').trim();
    if (!value) return;
    if (kind === 'name' && isMaskedPersonName(value)) return;
    const key = kind === 'photo' ? value.split('?')[0].toLowerCase() : value.toLowerCase();
    if (!map.has(key)) map.set(key, { value, sources: new Set() });
    if (sourceName) map.get(key)!.sources.add(sourceName);
  };

  (nodes || []).forEach((node) => {
    if (!node || isAggregateHubNode(node)) return;
    const metadata = node.metadata || {};
    const sourceName = String(metadata.platform || node.label || node.type || 'source');
    const searchable = metadata.searchable || metadata.searchableValues || {};
    if (kind === 'name') {
      const names = [
        ...(Array.isArray(searchable.name) ? searchable.name : []),
        ...(Array.isArray(searchable.names) ? searchable.names : []),
        ...(Array.isArray(metadata.displayOnly?.names) ? metadata.displayOnly.names : []),
        ...(Array.isArray(metadata.extractedNames) ? metadata.extractedNames : []),
        ...(Array.isArray(metadata.normalizedResult?.name) ? metadata.normalizedResult.name : []),
      ];
      names.forEach((name) => add(name, sourceName));
      return;
    }

    const images = [
      ...(Array.isArray(searchable.image) ? searchable.image : []),
      ...(Array.isArray(metadata.normalizedResult?.image) ? metadata.normalizedResult.image : []),
      node.imageUrl,
      metadata.imageUrl,
      metadata.previewImageUrl,
    ];
    images.forEach((url) => {
      if (isCollectablePhotoUrl(String(url), node)) add(url, sourceName);
    });
  });

  return Array.from(map.values()).map((entry) => ({
    value: entry.value,
    sources: Array.from(entry.sources),
  }));
}

function resolveCollectionHubParentId(
  nodes: any[],
  edges: any[],
  preferredRootId?: string,
): string {
  const nodeIds = new Set((nodes || []).map((node) => String(node?.id || '')).filter(Boolean));
  const preferred = String(preferredRootId || '').trim();
  if (preferred && nodeIds.has(preferred)) return preferred;

  const collectionParents = (edges || [])
    .filter((edge) => {
      const target = String(edge?.target || '');
      return target === 'aggregate:phones' || target === 'aggregate:emails';
    })
    .map((edge) => String(edge?.source || ''))
    .filter((id) => nodeIds.has(id));

  const starter = (nodes || []).find((node) => isStarterGraphNode(node));
  if (starter?.id && collectionParents.includes(String(starter.id))) return String(starter.id);

  const layer0 = (nodes || []).find((node) => Number(node?.metadata?.layer) === 0);
  if (layer0?.id && collectionParents.includes(String(layer0.id))) return String(layer0.id);

  if (collectionParents.length) {
    const ranked = [...collectionParents].sort((left, right) => {
      const leftLayer = Number(nodes.find((node) => node.id === left)?.metadata?.layer ?? 99);
      const rightLayer = Number(nodes.find((node) => node.id === right)?.metadata?.layer ?? 99);
      return leftLayer - rightLayer;
    });
    return ranked[0];
  }

  if (starter?.id) return String(starter.id);
  if (layer0?.id) return String(layer0.id);
  const sourceRoot = (nodes || []).find(
    (node) => String(node?.sourceType || '').toLowerCase() === 'root',
  );
  if (sourceRoot?.id) return String(sourceRoot.id);
  const phone = (nodes || []).find((node) => String(node?.type || '').toLowerCase() === 'phone');
  return String(phone?.id || nodes?.[0]?.id || '');
}

function upsertViewerCollectionHub(
  nodes: any[],
  edges: any[],
  spec: {
    id: string;
    type: string;
    collectionType: string;
    labelPrefix: string;
    edgeType: string;
    items: Array<{ value: string; sources: string[] }>;
    parentId: string;
  },
): { nodes: any[]; edges: any[] } {
  const list = [...nodes];
  const edgeList = [...edges];
  const existingIndex = list.findIndex((node) => String(node?.id) === spec.id);
  if (!spec.items.length) {
    if (existingIndex >= 0) {
      return {
        nodes: list.filter((node) => String(node?.id) !== spec.id),
        edges: edgeList.filter(
          (edge) => String(edge?.source) !== spec.id && String(edge?.target) !== spec.id,
        ),
      };
    }
    return { nodes: list, edges: edgeList };
  }

  const collectedValues = spec.items.map((item) => item.value);
  const searchableKey = spec.collectionType === 'photo' ? 'image' : spec.collectionType;
  const searchable = {
    name: searchableKey === 'name' ? collectedValues : [],
    email: [],
    image: searchableKey === 'image' ? collectedValues : [],
    phone: [],
    username: [],
  };
  const hub = {
    id: spec.id,
    label: `${spec.labelPrefix} (${collectedValues.length})`,
    type: spec.type,
    role: 'candidate',
    confidence: 0.9,
    evidenceTier: 'tier1',
    sourceType: 'osint',
    sourceReferences: [],
    imageUrl: null,
    attributionStatus: 'candidate',
    metadata: {
      isAggregateCollection: true,
      collectionType: spec.collectionType,
      items: spec.items,
      collectedValues,
      parentNodeId: spec.parentId,
      sourceContext: 'drill_down_aggregate_collection',
      searchable,
      searchableValues: searchable,
      searchableCount: collectedValues.length,
      // Aggregates are view-only (R10–R12); never invent OSINT/Leak/Face here.
      availableActions: [],
      availableActionsByValueType: {},
      drillDownExpandable: false,
      layer: 1,
    },
  };

  if (existingIndex >= 0) {
    list[existingIndex] = { ...list[existingIndex], ...hub, metadata: { ...(list[existingIndex].metadata || {}), ...hub.metadata } };
  } else {
    list.push(hub);
  }

  const rewiredEdges = edgeList.filter(
    (edge) => String(edge?.source) !== spec.id && String(edge?.target) !== spec.id,
  );
  if (spec.parentId) {
    rewiredEdges.push({
      id: `${spec.parentId}::${spec.edgeType}::${spec.id}`,
      source: spec.parentId,
      target: spec.id,
      type: spec.edgeType,
      label: spec.edgeType.replace(/_/g, ' '),
      confidence: 0.9,
      evidenceTier: 'tier1',
      sourceType: 'osint',
      sourceReferences: [],
      metadata: { aggregateCollection: true },
    });
  }

  return { nodes: list, edges: rewiredEdges };
}

/**
 * Legacy viewer hub for names. Photos stay in each source's detail panel.
 * Phase 7: when the server already sent ladder aggregates, do not invent client hubs.
 */
export function ensureNamesAndPhotosCollectionHubs(
  nodes: any[],
  edges: any[],
  preferredRootId?: string,
): { nodes: any[]; edges: any[] } {
  const list = Array.isArray(nodes) ? nodes.filter((node) => node?.id) : [];
  const edgeList = Array.isArray(edges) ? [...edges] : [];
  if (hasLadderAggregateNodes(list)) {
    return { nodes: list, edges: edgeList };
  }
  const parentId = resolveCollectionHubParentId(list, edgeList, preferredRootId);
  if (!parentId) return { nodes: list, edges: edgeList };

  const withNames = upsertViewerCollectionHub(list, edgeList, {
    id: 'aggregate:names',
    type: 'aggregate_names',
    collectionType: 'name',
    labelPrefix: 'Names',
    edgeType: 'has_names',
    items: collectNamedItems(list, 'name'),
    parentId,
  });
  return withNames;
}

export function mapPayloadToViewerGraph(
  payload: any,
  options?: { rootValue?: string },
): ViewerGraphData {
  const rootValue = resolveGraphRootValue(payload, options?.rootValue);
  // Project supporting photo records into their source's gallery.
  const prepared = foldCollectedPhotoNodes(payload?.nodes || [], payload?.edges || []);
  const withCollections = ensureNamesAndPhotosCollectionHubs(
    prepared.nodes,
    prepared.edges,
    payload?.rootNode?.id || payload?.rootNodeId,
  );
  const nodes: ViewerNode[] = withCollections.nodes
    .filter((node: any) => node?.id)
    .map((node: any) => ({
      id: node.id,
      label:
        isStarterGraphNode(node) && rootValue
          ? rootValue
          : String(node.label || node.metadata?.queryValue || node.metadata?.handle || node.id),
      type: mapApiType(
        node.type,
        node.type === 'query_root' ? node.metadata?.queryType || node.role : node.role,
      ),
      details: extractDetails(node),
      image: extractImage(node),
      firstSeen: node.metadata?.firstSeen || node.createdAt || node.updatedAt,
      coords: extractCoords(node),
      confidence:
        node.confidence != null
          ? node.confidence <= 1
            ? Math.round(node.confidence * 100)
            : Math.round(node.confidence)
          : undefined,
      risk: node.risk,
      raw: node,
    }));

  const nodeIds = new Set(nodes.map((n) => n.id));
  const edges: ViewerEdge[] = withCollections.edges
    .map((edge: any) => ({
      source: normalizeEdgeEndpoint(edge?.source),
      target: normalizeEdgeEndpoint(edge?.target),
      label: String(edge?.label || edge?.type || 'linked'),
      type: edge?.type ? String(edge.type) : undefined,
      role: edge?.role ? String(edge.role) : undefined,
      confidence:
        edge?.confidence != null
          ? edge.confidence <= 1
            ? Math.round(edge.confidence * 100)
            : Math.round(edge.confidence)
          : undefined,
      evidenceTier: edge?.evidenceTier ? String(edge.evidenceTier) : undefined,
      raw: edge,
    }))
    .filter((edge: ViewerEdge) => edge.source && edge.target && nodeIds.has(edge.source) && nodeIds.has(edge.target));

  return { nodes, edges };
}

export function getBrand(node: ViewerNode | undefined): { color: string; short: string } | null {
  if (!node || node.type !== 'social') return null;
  const platform = node.details?.['platform'] || node.label || '';
  const key = resolveBrandKeyFromPlatform(platform) || node.id.replace(/^social-/, '');
  return PLATFORM_BRAND[key] ?? null;
}

export function buildAdjacency(data: ViewerGraphData): Map<string, Set<string>> {
  const adj = new Map<string, Set<string>>();
  data.edges.forEach((e) => {
    if (!adj.has(e.source)) adj.set(e.source, new Set());
    if (!adj.has(e.target)) adj.set(e.target, new Set());
    adj.get(e.source)!.add(e.target);
    adj.get(e.target)!.add(e.source);
  });
  return adj;
}

export function getDegreeMap(data: ViewerGraphData): Record<string, number> {
  const counts: Record<string, number> = {};
  data.edges.forEach((e) => {
    counts[e.source] = (counts[e.source] ?? 0) + 1;
    counts[e.target] = (counts[e.target] ?? 0) + 1;
  });
  return counts;
}

export function deriveMeta(
  node: ViewerNode,
  degree: number,
): { confidence: number; risk: 'low' | 'medium' | 'high' } {
  if (node.confidence != null && node.risk != null) {
    return { confidence: node.confidence, risk: node.risk };
  }
  const base: Record<EntityType, number> = {
    person: 95,
    email: 92,
    phone: 96,
    social: 70,
    location: 65,
    image: 60,
  };
  const confidence = Math.min(99, (base[node.type] ?? 60) + Math.min(degree * 2, 12));
  const risk: 'low' | 'medium' | 'high' =
    node.type === 'person' || node.type === 'phone' || node.type === 'email'
      ? degree >= 5
        ? 'high'
        : 'medium'
      : degree >= 4
        ? 'medium'
        : 'low';
  return { confidence, risk };
}

export function buildHierarchy(data: ViewerGraphData, rootId: string): TreeNode | null {
  const nodeMap = new Map(data.nodes.map((n) => [n.id, n]));
  const root = nodeMap.get(rootId) || data.nodes[0];
  if (!root) return null;

  // Phase 7: parentage from parentNodeId (+ directed edges as fallback). Never value-match to root.
  const childrenByParent = new Map<string, Array<{ id: string; label: string }>>();
  const claimed = new Set<string>();

  const addChild = (parentId: string, childId: string, relation: string) => {
    if (!parentId || !childId || parentId === childId) return;
    if (!nodeMap.has(parentId) || !nodeMap.has(childId)) return;
    if (claimed.has(childId)) return;
    claimed.add(childId);
    if (!childrenByParent.has(parentId)) childrenByParent.set(parentId, []);
    childrenByParent.get(parentId)!.push({ id: childId, label: relation });
  };

  data.nodes.forEach((node) => {
    if (node.id === root.id) return;
    const parentId = String(node.raw?.metadata?.parentNodeId || '').trim();
    if (!parentId) return;
    addChild(parentId, node.id, 'linked');
  });

  data.edges.forEach((edge) => {
    const source = String(edge.source || '');
    const target = String(edge.target || '');
    if (!source || !target || claimed.has(target)) return;
    // Only attach via directed edge when the child has no parentNodeId yet.
    const targetMetaParent = String(nodeMap.get(target)?.raw?.metadata?.parentNodeId || '').trim();
    if (targetMetaParent) return;
    addChild(source, target, edge.label || 'linked');
  });

  const typePriority: Record<EntityType, number> = {
    person: 0,
    email: 1,
    phone: 2,
    social: 3,
    location: 4,
    image: 5,
  };

  const visited = new Set<string>([root.id]);
  const rootTree: TreeNode = { node: root, children: [], depth: 0 };
  const queue: TreeNode[] = [rootTree];

  while (queue.length > 0) {
    const current = queue.shift()!;
    const neighbors = (childrenByParent.get(current.node.id) ?? []).slice();
    neighbors.sort((a, b) => {
      const na = nodeMap.get(a.id);
      const nb = nodeMap.get(b.id);
      const layerA = Number(na?.raw?.metadata?.layer ?? 99);
      const layerB = Number(nb?.raw?.metadata?.layer ?? 99);
      if (layerA !== layerB) return layerA - layerB;
      return (typePriority[na?.type as EntityType] ?? 9) - (typePriority[nb?.type as EntityType] ?? 9);
    });
    neighbors.forEach((nb) => {
      if (visited.has(nb.id)) return;
      const childNode = nodeMap.get(nb.id);
      if (!childNode) return;
      visited.add(nb.id);
      const child: TreeNode = {
        node: childNode,
        children: [],
        relation: nb.label,
        depth: current.depth + 1,
      };
      current.children.push(child);
      queue.push(child);
    });
  }

  return rootTree;
}

export function getHierarchyEdges(data: ViewerGraphData, rootId: string): ViewerEdge[] {
  const tree = buildHierarchy(data, rootId);
  const result: ViewerEdge[] = [];
  const originalEdges = new Map<string, ViewerEdge>();
  data.edges.forEach((edge) => {
    const key = JSON.stringify([edge.source, edge.target]);
    if (!originalEdges.has(key)) originalEdges.set(key, edge);
  });
  if (!tree) return result;
  const walk = (t: TreeNode) => {
    t.children.forEach((c) => {
      const original = originalEdges.get(JSON.stringify([t.node.id, c.node.id]));
      result.push({ ...original, source: t.node.id, target: c.node.id, label: original?.label ?? c.relation ?? 'linked' });
      walk(c);
    });
  };
  walk(tree);
  return result;
}

export function pruneTreeForVisibleNodes(
  tree: TreeNode | null,
  visibleIds: Set<string>,
): TreeNode | null {
  if (!tree || !visibleIds.has(tree.node.id)) return null;
  const children = tree.children
    .map((child) => pruneTreeForVisibleNodes(child, visibleIds))
    .filter((child): child is TreeNode => child !== null);
  return { ...tree, children };
}

/** Find a node in the hierarchy tree by id. */
export function findTreeNode(tree: TreeNode | null, nodeId: string): TreeNode | null {
  if (!tree) return null;
  if (tree.node.id === nodeId) return tree;
  for (const child of tree.children) {
    const found = findTreeNode(child, nodeId);
    if (found) return found;
  }
  return null;
}

/** Direct parent of a node in the hierarchy tree, if any. */
export function findTreeParentNode(tree: TreeNode | null, nodeId: string): TreeNode | null {
  if (!tree) return null;
  for (const child of tree.children) {
    if (child.node.id === nodeId) return tree;
    const found = findTreeParentNode(child, nodeId);
    if (found) return found;
  }
  return null;
}

/** Dragged node id plus all descendant ids in the hierarchy subtree. */
export function collectTreeSubtreeIds(tree: TreeNode | null, nodeId: string): Set<string> {
  const result = new Set<string>();
  const root = findTreeNode(tree, nodeId);
  if (!root) {
    result.add(nodeId);
    return result;
  }
  const walk = (current: TreeNode): void => {
    result.add(current.node.id);
    current.children.forEach(walk);
  };
  walk(root);
  return result;
}

/** Selected node plus direct hierarchy parent and children (visible ids only). */
export function getHierarchySelectionFocusIds(
  tree: TreeNode | null,
  nodeId: string,
  visibleIds?: Set<string>,
): Set<string> {
  const result = new Set<string>([nodeId]);
  if (!tree) return result;

  const treeNode = ((): TreeNode | null => {
    const walk = (current: TreeNode): TreeNode | null => {
      if (current.node.id === nodeId) return current;
      for (const child of current.children) {
        const found = walk(child);
        if (found) return found;
      }
      return null;
    };
    return walk(tree);
  })();

  const parent = findTreeParentNode(tree, nodeId);
  if (parent && (!visibleIds || visibleIds.has(parent.node.id))) {
    result.add(parent.node.id);
  }

  treeNode?.children.forEach((child) => {
    if (!visibleIds || visibleIds.has(child.node.id)) {
      result.add(child.node.id);
    }
  });

  return result;
}

interface TreeLayoutUnit {
  id: string;
  footprint: number;
  children: TreeLayoutUnit[];
  subtreeWidth: number;
}

function getTreeNodeFootprint(node: ViewerNode, degreeMap: Record<string, number>): number {
  const count = degreeMap[node.id] ?? 1;
  const baseSize = node.type === 'person' ? 9 : node.type === 'phone' || node.type === 'email' ? 7 : 5;
  const radius = baseSize + Math.min(count * 0.7, 6);
  const diameter = radius * 2;
  const labelWidth = Math.min((node.label?.length ?? 0) * 6.5, 220);
  return Math.max(diameter + 20, labelWidth + 24, 52);
}

function getLayoutFootprint(
  node: ViewerNode,
  degreeMap: Record<string, number>,
  fixedNodeFootprint?: number,
): number {
  return fixedNodeFootprint ?? getTreeNodeFootprint(node, degreeMap);
}

function splitIntoBalancedRows<T>(items: T[], maxChildrenPerRow: number): T[][] {
  if (items.length <= maxChildrenPerRow) return [items];
  const rowCount = Math.ceil(items.length / maxChildrenPerRow);
  const minPerRow = Math.floor(items.length / rowCount);
  const extra = items.length % rowCount;
  const rows: T[][] = [];
  let cursor = 0;

  for (let row = 0; row < rowCount; row += 1) {
    const rowSize = minPerRow + (row < extra ? 1 : 0);
    rows.push(items.slice(cursor, cursor + rowSize));
    cursor += rowSize;
  }

  return rows;
}

function toTreeLayoutUnit(
  tree: TreeNode,
  degreeMap: Record<string, number>,
  fixedNodeFootprint?: number,
): TreeLayoutUnit {
  return {
    id: tree.node.id,
    footprint: getLayoutFootprint(tree.node, degreeMap, fixedNodeFootprint),
    children: tree.children.map((child) => toTreeLayoutUnit(child, degreeMap, fixedNodeFootprint)),
    subtreeWidth: 0,
  };
}

function measureSubtreeWidth(
  unit: TreeLayoutUnit,
  siblingGap: number,
  maxChildrenPerRow?: number,
): number {
  if (!unit.children.length) {
    unit.subtreeWidth = unit.footprint;
    return unit.subtreeWidth;
  }
  const childWidths = unit.children.map((child) =>
    measureSubtreeWidth(child, siblingGap, maxChildrenPerRow),
  );

  let childrenSpan = 0;
  if (maxChildrenPerRow && unit.children.length > maxChildrenPerRow) {
    const balancedRows = splitIntoBalancedRows(childWidths, maxChildrenPerRow);
    balancedRows.forEach((rowWidths) => {
      const rowSpan =
        rowWidths.reduce((sum, width) => sum + width, 0) +
        siblingGap * Math.max(0, rowWidths.length - 1);
      childrenSpan = Math.max(childrenSpan, rowSpan);
    });
  } else {
    childrenSpan =
      childWidths.reduce((sum, width) => sum + width, 0) +
      siblingGap * Math.max(0, unit.children.length - 1);
  }

  unit.subtreeWidth = Math.max(unit.footprint, childrenSpan);
  return unit.subtreeWidth;
}

function assignTreeLayoutPositions(
  unit: TreeLayoutUnit,
  centerX: number,
  currentY: number,
  siblingGap: number,
  levelDistance: number,
  rowHeight: number,
  maxChildrenPerRow: number | undefined,
  positions: Record<string, { x: number; y: number }>,
): void {
  positions[unit.id] = { x: centerX, y: currentY };
  if (!unit.children.length) return;

  const childEntries = unit.children.map((child) => ({ child, width: child.subtreeWidth }));
  if (maxChildrenPerRow && unit.children.length > maxChildrenPerRow) {
    const balancedRows = splitIntoBalancedRows(childEntries, maxChildrenPerRow);
    balancedRows.forEach((rowEntries, row) => {
      const rowWidths = rowEntries.map((entry) => entry.width);
      const rowSpan =
        rowWidths.reduce((sum, width) => sum + width, 0) +
        siblingGap * Math.max(0, rowWidths.length - 1);
      let cursor = centerX - rowSpan / 2;
      const rowY = currentY + levelDistance + row * rowHeight;

      rowEntries.forEach(({ child }, index) => {
        const childCenter = cursor + rowWidths[index] / 2;
        assignTreeLayoutPositions(
          child,
          childCenter,
          rowY,
          siblingGap,
          levelDistance,
          rowHeight,
          maxChildrenPerRow,
          positions,
        );
        cursor += rowWidths[index] + siblingGap;
      });
    });
    return;
  }

  const childWidths = childEntries.map((entry) => entry.width);
  const totalSpan =
    childWidths.reduce((sum, width) => sum + width, 0) +
    siblingGap * Math.max(0, unit.children.length - 1);
  let cursor = centerX - totalSpan / 2;

  unit.children.forEach((child, index) => {
    const childCenter = cursor + childWidths[index] / 2;
    assignTreeLayoutPositions(
      child,
      childCenter,
      currentY + levelDistance,
      siblingGap,
      levelDistance,
      rowHeight,
      maxChildrenPerRow,
      positions,
    );
    cursor += childWidths[index] + siblingGap;
  });
}

export function computeStaticTreeLayout(
  tree: TreeNode | null,
  options: {
    degreeMap: Record<string, number>;
    siblingGap?: number;
    levelDistance?: number;
    maxChildrenPerRow?: number;
    rowHeight?: number;
    fixedNodeFootprint?: number;
  },
): Record<string, { x: number; y: number }> {
  if (!tree) return {};

  const siblingGap = options.siblingGap ?? 28;
  const levelDistance = options.levelDistance ?? 130;
  const rowHeight = options.rowHeight ?? Math.max(72, Math.round(levelDistance * 0.68));
  const layoutRoot = toTreeLayoutUnit(tree, options.degreeMap, options.fixedNodeFootprint);
  measureSubtreeWidth(layoutRoot, siblingGap, options.maxChildrenPerRow);

  const positions: Record<string, { x: number; y: number }> = {};
  assignTreeLayoutPositions(
    layoutRoot,
    0,
    0,
    siblingGap,
    levelDistance,
    rowHeight,
    options.maxChildrenPerRow,
    positions,
  );
  return positions;
}

export function getTreeMaxDepth(tree: TreeNode | null): number {
  if (!tree) return 0;
  return tree.children.reduce((max, child) => Math.max(max, getTreeMaxDepth(child)), tree.depth);
}

export function findShortestPath(
  data: ViewerGraphData,
  sourceId: string,
  targetId: string,
): PathResult | null {
  if (sourceId === targetId) return { path: [sourceId], edgeLabels: [] };
  const adj = new Map<string, { id: string; label: string }[]>();
  data.edges.forEach((e) => {
    if (!adj.has(e.source)) adj.set(e.source, []);
    if (!adj.has(e.target)) adj.set(e.target, []);
    adj.get(e.source)!.push({ id: e.target, label: e.label });
    adj.get(e.target)!.push({ id: e.source, label: e.label });
  });
  const visited = new Set<string>([sourceId]);
  const queue: { id: string; trail: { id: string; label: string }[] }[] = [{ id: sourceId, trail: [] }];
  while (queue.length > 0) {
    const cur = queue.shift()!;
    for (const nb of adj.get(cur.id) ?? []) {
      if (visited.has(nb.id)) continue;
      const newTrail = [...cur.trail, { id: nb.id, label: nb.label }];
      if (nb.id === targetId) {
        return {
          path: [sourceId, ...newTrail.map((t) => t.id)],
          edgeLabels: newTrail.map((t) => t.label),
        };
      }
      visited.add(nb.id);
      queue.push({ id: nb.id, trail: newTrail });
    }
  }
  return null;
}

export function detectCorrelations(data: ViewerGraphData): Correlation[] {
  const { nodes, edges } = data;
  const results: Correlation[] = [];

  const usernameMap = new Map<string, string[]>();
  nodes.forEach((n) => {
    const uname = n.details?.['username'];
    if (uname && n.type === 'social') {
      const key = uname.toLowerCase().replace(/\s+/g, '');
      if (!usernameMap.has(key)) usernameMap.set(key, []);
      usernameMap.get(key)!.push(n.id);
    }
  });
  usernameMap.forEach((ids, key) => {
    if (ids.length >= 2) {
      const displayName = nodes.find((n) => ids.includes(n.id))?.details?.['username'] ?? key;
      results.push({
        attribute: `Username: ${displayName}`,
        type: 'username',
        nodeIds: ids,
        strength: ids.length >= 4 ? 'strong' : 'moderate',
      });
    }
  });

  const locationLinks = new Map<string, string[]>();
  edges.forEach((e) => {
    const src = nodes.find((n) => n.id === e.source);
    const tgt = nodes.find((n) => n.id === e.target);
    if (src?.type === 'location') {
      if (!locationLinks.has(src.id)) locationLinks.set(src.id, []);
      locationLinks.get(src.id)!.push(tgt?.id ?? e.target);
    }
    if (tgt?.type === 'location') {
      if (!locationLinks.has(tgt.id)) locationLinks.set(tgt.id, []);
      locationLinks.get(tgt.id)!.push(src?.id ?? e.source);
    }
  });
  locationLinks.forEach((ids, locId) => {
    if (ids.length >= 2) {
      const loc = nodes.find((n) => n.id === locId);
      results.push({
        attribute: `Location: ${loc?.label ?? locId}`,
        type: 'location',
        nodeIds: [locId, ...ids],
        strength: ids.length >= 3 ? 'strong' : 'moderate',
      });
    }
  });

  const faceNodes = new Set<string>();
  edges.forEach((e) => {
    if (e.label === 'face match' || e.label === 'profile photo') {
      faceNodes.add(e.source);
      faceNodes.add(e.target);
    }
  });
  if (faceNodes.size >= 2) {
    results.push({
      attribute: 'Face match across platforms',
      type: 'face_match',
      nodeIds: Array.from(faceNodes),
      strength: 'strong',
    });
  }

  const emailLinks = new Map<string, string[]>();
  edges.forEach((e) => {
    const src = nodes.find((n) => n.id === e.source);
    const tgt = nodes.find((n) => n.id === e.target);
    if (src?.type === 'email') {
      if (!emailLinks.has(src.id)) emailLinks.set(src.id, []);
      emailLinks.get(src.id)!.push(tgt?.id ?? e.target);
    }
    if (tgt?.type === 'email') {
      if (!emailLinks.has(tgt.id)) emailLinks.set(tgt.id, []);
      emailLinks.get(tgt.id)!.push(src?.id ?? e.source);
    }
  });
  emailLinks.forEach((ids, emailId) => {
    if (ids.length >= 3) {
      const em = nodes.find((n) => n.id === emailId);
      results.push({
        attribute: `Email: ${em?.label ?? emailId}`,
        type: 'email',
        nodeIds: [emailId, ...ids],
        strength: ids.length >= 6 ? 'strong' : 'moderate',
      });
    }
  });

  return results.sort(
    (a, b) => (a.strength === 'strong' ? -1 : 1) - (b.strength === 'strong' ? -1 : 1),
  );
}

export function getTimelineDates(data: ViewerGraphData): string[] {
  const dates = new Set<string>();
  data.nodes.forEach((n) => {
    if (n.firstSeen) {
      const d = String(n.firstSeen).slice(0, 10);
      if (d) dates.add(d);
    }
  });
  if (!dates.size) {
    return [new Date().toISOString().slice(0, 10)];
  }
  return Array.from(dates).sort();
}

export function getHopDistances(data: ViewerGraphData, rootId: string): Record<string, number> {
  const adj = buildAdjacency(data);
  const dist: Record<string, number> = { [rootId]: 0 };
  const queue: string[] = [rootId];
  while (queue.length > 0) {
    const cur = queue.shift()!;
    for (const nb of Array.from(adj.get(cur) ?? [])) {
      if (dist[nb] === undefined) {
        dist[nb] = dist[cur] + 1;
        queue.push(nb);
      }
    }
  }
  return dist;
}

function csvEscape(v: string): string {
  const s = String(v ?? '');
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

export function entitiesToCSV(data: ViewerGraphData): string {
  const degree = getDegreeMap(data);
  const header = ['id', 'label', 'type', 'connections', 'confidence', 'risk', 'firstSeen', 'details'];
  const rows = data.nodes.map((n) => {
    const meta = deriveMeta(n, degree[n.id] ?? 0);
    const detailStr = Object.entries(n.details ?? {})
      .map(([k, v]) => `${k}=${v}`)
      .join('; ');
    return [n.id, n.label, ENTITY_LABELS[n.type], String(degree[n.id] ?? 0), String(meta.confidence), meta.risk, n.firstSeen ?? '', detailStr]
      .map(csvEscape)
      .join(',');
  });
  return [header.join(','), ...rows].join('\n');
}

export function relationshipsToCSV(data: ViewerGraphData): string {
  const map = new Map(data.nodes.map((n) => [n.id, n.label]));
  const header = ['source_id', 'source_label', 'relationship', 'target_id', 'target_label'];
  const rows = data.edges.map((e) =>
    [e.source, map.get(e.source) ?? e.source, e.label, e.target, map.get(e.target) ?? e.target]
      .map(csvEscape)
      .join(','),
  );
  return [header.join(','), ...rows].join('\n');
}

export function exportToJSON(data: ViewerGraphData, subject?: { phone?: string; email?: string; name?: string }): string {
  const degree = getDegreeMap(data);
  return JSON.stringify(
    {
      subject: subject ?? {},
      generatedAt: new Date().toISOString(),
      entities: data.nodes.map((n) => ({
        ...n,
        connections: degree[n.id] ?? 0,
        ...deriveMeta(n, degree[n.id] ?? 0),
      })),
      relationships: data.edges,
      correlations: detectCorrelations(data),
    },
    null,
    2,
  );
}

export function resolveDefaultRootId(data: ViewerGraphData, preferred?: string): string {
  if (preferred && data.nodes.some((n) => n.id === preferred)) return preferred;
  const queryRoot = data.nodes.find(
    (n) =>
      String(n.raw?.type || '').toLowerCase() === 'query_root' ||
      String(n.id || '').startsWith('query_root:'),
  );
  if (queryRoot) return queryRoot.id;
  const starter = data.nodes.find((n) => n.raw?.type === 'starter' || n.id === 'starter');
  if (starter) return starter.id;
  const layer0 = data.nodes.find((n) => Number(n.raw?.metadata?.layer) === 0);
  if (layer0) return layer0.id;
  const phone = data.nodes.find((n) => n.type === 'phone');
  if (phone) return phone.id;
  const email = data.nodes.find((n) => n.type === 'email');
  if (email) return email.id;
  return data.nodes[0]?.id ?? '';
}
